import {
  feedbackcard,
  Field,
  FilterQuery,
  formatDateTime,
  ProjectFields,
  YesObject
} from '@yes.technology/react-toolkit'
import zip from 'lodash/zip'
import { useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { useSelector } from 'react-redux'
import { FilterFieldsResponse } from 'shared/hooks/useFilterFields/useFilterFields'
import { getApplicationSystemInfo } from 'shared/utils'
import { AllowedLocales } from 'shared/utils/date/formatDate'
import {
  FieldValuesValue,
  GroupedFieldValues
} from 'site/Renderer/components/FilterFields/types'
import { useSiteState } from 'siteState/shared'
import { writeFile, utils as xlsUtils } from 'xlsx'
import fetchYesObjects from 'yesObject/shared/api/fetchYesObjects'
import { FieldType } from '../useFormattedValue'
import useTableContext from '../useTableContext'

export const MAX_SHEET_NAME_LENGTH = 31

const SHEET_NAME_PROHIBITED_CHARS = /((^')|[/\\?*:[\]]|('$))/g

/**
 * Formats sheet name according to rules listed in
 * https://support.microsoft.com/en-us/office/rename-a-worksheet-3f1f7148-ee83-404d-8ef0-9ff99fbad1f9
 *
 * Worksheet names cannot:
 *  Be blank.
 *  Contain more than 31 characters.
 *  Contain any of the following characters: / \ ? * : [ ]
 *  For example, 02/17/2016 would not be a valid worksheet name, but 02-17-2016 would work fine.
 *  Begin or end with an apostrophe ('), but they can be used in between text or numbers in a name.
 *  Be named "History". This is a reserved word Excel uses internally.
 **/
const formatSheetName = (name: string, fixedSuffix: string) => {
  const formatedName = name.replaceAll(SHEET_NAME_PROHIBITED_CHARS, '-')

  const maximumNameLength = MAX_SHEET_NAME_LENGTH - fixedSuffix.length
  const truncatedName =
    formatedName.length <= maximumNameLength
      ? formatedName
      : `${formatedName.slice(0, maximumNameLength - 3)}...`

  return `${truncatedName}${fixedSuffix}`
}

const MAX_XLS_CELL_LENGTH = 32767

const formatCellValue = (
  value: YesObject[string],
  type?: FieldType,
  locale?: AllowedLocales
) => {
  const stringValue = (() => {
    if (value instanceof Array) return value.map((v) => v.des).join(', ')

    return type === 'DATETIME'
      ? formatDateTime(new Date(value), {
          format: 'date-time',
          locale,
          timezone: globalThis.waTimezone
        })
      : value
  })()

  return stringValue?.length > MAX_XLS_CELL_LENGTH
    ? `${stringValue.slice(0, MAX_XLS_CELL_LENGTH - 3)}...`
    : stringValue
}

const formatObjects = (
  objects: YesObject[],
  columns: string[],
  columnsMeta: Record<string, { label: string; type: FieldType }>,
  locale: AllowedLocales
) => {
  return objects.map((object) => {
    return columns.reduce(
      (acc, column) => {
        return {
          ...acc,
          [columnsMeta[column].label]: formatCellValue(
            object[column],
            columnsMeta[column].type,
            locale
          )
        }
      },
      {} as Record<string, string>
    )
  })
}

const formatFilterFieldValue = (fieldValue: FieldValuesValue) => {
  if (fieldValue instanceof Array) {
    return fieldValue.map((value) => formatCellValue(value.des))
  }
  return [formatCellValue(fieldValue)]
}

export default function useExportXLS() {
  const [filterQuery] = useSiteState<FilterQuery>('filter-query')
  const [objectclassUUID] = useSiteState<string>('objectclassuuid')
  const [fieldValues] = useSiteState<GroupedFieldValues>('field-values')
  const [filterFields] = useSiteState<FilterFieldsResponse>('filter-fields')
  const [filterComposition] = useSiteState<string>('field-composition')
  const [filteractionName] = useSiteState<string>('filteraction-name')
  const [filterExecutionTime] = useSiteState<Date>('filter-execution-time')
  const [sharedProjectFields] = useSiteState<ProjectFields>('project-fields')
  const [isLoading, setIsLoading] = useState(false)
  const [batchIndex, setBatchIndex] = useState(0)

  const { t, language } = useTranslation()
  const locale = language as AllowedLocales

  const username: string = useSelector(
    ({ authentication }) => authentication?.username
  )

  const { allColumns, sortOptions, fields = {} } = useTableContext()

  const formatFiltersForXLS = ({
    objectclassName
  }: {
    objectclassName: string
  }) => {
    const { classifiedFields } = filterFields || {}

    const classifiedFieldsFlat = classifiedFields?.flat() || []

    const fieldByKey = Object.fromEntries(
      classifiedFieldsFlat.map((field: Field) => [field?.column_json, field])
    )

    const formattedFilters = Object.entries(fieldValues || {}).reduce(
      (acc, [groupKey, fields]): Array<Array<string | undefined>> => {
        const group = Object.entries(fields).reduce(
          (groupAcc, [fieldKey, field]) => {
            const fieldData = fieldByKey[fieldKey]
            if (fieldData) {
              const label = fieldData.display_text.des
              const value = formatFilterFieldValue(field.value)
              return { ...groupAcc, [label]: value }
            }
            return groupAcc
          },
          {}
        )

        return [
          ...acc,
          [`Grupo de Filtros ${groupKey}`],
          [],
          Object.keys(group),
          ...zip(...(Object.values(group) as Array<string | undefined>[])),
          [],
          []
        ]
      },
      [] as Array<Array<string | undefined>>
    )

    const groupNumberRegex = /([0-9]+)/g
    const formattedComposition = filterComposition
      ?.replaceAll(
        groupNumberRegex,
        t('xls-report.metadata-sheet.filter-group') + ` $&`
      )
      ?.replace(/\b(AND|OR)\b/gi, (match) =>
        match.toUpperCase() === 'AND'
          ? t('composition-operator.and')
          : t('composition-operator.or')
      )

    return [
      [`${objectclassName} | Filtros`],
      [],
      [
        t('xls-report.metadata-sheet.filteraction'),
        t('xls-report.metadata-sheet.filteraction-composition'),
        t('xls-report.metadata-sheet.filter-execution-time')
      ],
      [
        filteractionName,
        formattedComposition,
        formatDateTime(filterExecutionTime, {
          format: 'date-time',
          locale,
          timezone: globalThis.waTimezone
        })
      ],
      [],
      [],
      ...formattedFilters
    ]
  }

  const formatMetadataForXLS = ({
    objectclassName,
    username
  }: {
    objectclassName: string
    username: string
  }) => {
    const filters = formatFiltersForXLS({ objectclassName })

    const { ownerDes } = getApplicationSystemInfo()

    return [
      [t('xls-report.metadata-sheet.title')],
      [],
      [],
      [t('xls-report.metadata-sheet.management-info')],
      [],
      [
        t('xls-report.metadata-sheet.report-model'),
        t('xls-report.metadata-sheet.report-issuer'),
        t('xls-report.metadata-sheet.application-system-owner'),
        t('xls-report.metadata-sheet.language'),
        t('xls-report.metadata-sheet.timezone')
      ],
      [
        `Master Data | ${objectclassName}`,
        username,
        ownerDes,
        t('language'),
        Intl.DateTimeFormat().resolvedOptions().timeZone
      ],
      [],
      [],
      ...filters
    ]
  }

  const columnsMeta = Object.entries(fields).reduce<
    Record<string, { label: string; type: FieldType }>
  >((acc, [columnName, field]) => {
    return {
      ...acc,
      [columnName]: { label: field.display_text.des, type: field.type }
    }
  }, {})

  const exportXLSReport = async (batchSize = 500) => {
    if (!allColumns) return

    setIsLoading(true)
    setBatchIndex(0)

    try {
      const hasProjectFields = Object.keys(sharedProjectFields).length > 0

      let allObjects: YesObject[] = []
      let offset = 0
      let hasMore = true
      let objectclassName = ''

      while (hasMore) {
        const filterBody = {
          query: filterQuery,
          objectclass: objectclassUUID,
          data_storage: '',
          orderby: sortOptions,
          pagination: {
            limit: batchSize,
            offset
          },
          ...(hasProjectFields ? { project_fields: sharedProjectFields } : {})
        }

        const { objects, metadata, pagination } = await fetchYesObjects({
          filterBody,
          showLoading: false
        })

        if (offset === 0) {
          objectclassName = metadata.des
        }

        if (objects && objects.length > 0) {
          allObjects = [...allObjects, ...objects]
        }

        setBatchIndex((prev) => prev + 1)

        hasMore = pagination?.hasNext || false
        offset += batchSize
      }

      const xlsMetadata = formatMetadataForXLS({
        objectclassName,
        username
      })

      const formattedObjects = formatObjects(
        allObjects,
        allColumns,
        columnsMeta,
        locale
      )

      const metadataSheet = xlsUtils.aoa_to_sheet(xlsMetadata)
      const dataSheet = xlsUtils.json_to_sheet(formattedObjects)
      const wb = xlsUtils.book_new()

      xlsUtils.book_append_sheet(
        wb,
        metadataSheet,
        t('xls-report.metadata-sheet.tab-title')
      )

      const dataSheetTitle = ` | ${t('xls-report.data-sheet.tab-title')}`

      xlsUtils.book_append_sheet(
        wb,
        dataSheet,
        formatSheetName(objectclassName, dataSheetTitle)
      )

      writeFile(wb, 'report.xlsx')
    } catch (error: unknown) {
      console.error(error)

      feedbackcard(t('report.download-error-title'), {
        message: t('report.download-error-message'),
        type: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return {
    exportXLSReport,
    isLoading,
    hasRequiredReportData: !!filterQuery,
    batchIndex: batchIndex + 1
  }
}
